<template>
  <div v-if="showToolbar" class="table-toolbar">
    <div class="left-wrapper">
      <slot name="btns" />
      <div v-if="showColumnFilter" class="column-filter">
        <el-popover
          popper-class="filter-popover"
          placement="right-start"
          trigger="click"
          :width="width"
        >
          <template #reference>
            <img src="../../../assets/images/table/column_filter.png" alt="" />
          </template>
          <div class="list-wrapper">
            <div class="list-title">
              {{ $t('列表数据') }}
            </div>
            <div class="list-data">
              <el-row>
                <el-col class="check-all" :span="24">
                  <el-checkbox
                    v-model="checkAll"
                    :indeterminate="isIndeterminate"
                    :label="$t('全部')"
                    @change="handleCheckAllChange"
                  />
                </el-col>
              </el-row>
              <el-checkbox-group v-model="checkList" @change="handleCheckedOptionsChange">
                <div class="list-row">
                  <div v-for="(arr, i) in dataList" :key="i" class="list-col">
                    <template v-for="(item, index) in arr" :key="index">
                      <el-checkbox :disabled="item.require" :label="$t(item.label)" />
                    </template>
                  </div>
                </div>
              </el-checkbox-group>
            </div>
          </div>
        </el-popover>
      </div>
    </div>
    <div v-if="showQuery" class="search-wrapper">
      <img src="../../../assets/images/table/search.png" alt="" />
      <div v-if="$t(queryPlaceholder).length > 15">
        <el-tooltip
          effect="dark"
          :content="$t(queryPlaceholder)"
          placement="top"
          :trigger-keys="['Enter']"
        >
          <input
            ref="searVal"
            type="text"
            :value="keyword"
            :placeholder="$t(queryPlaceholder)"
            @keyup.enter.prevent="handleInput"
            @input="handleonInput"
          />
        </el-tooltip>
      </div>

      <input
        v-else
        ref="searVal"
        type="text"
        :value="keyword"
        :placeholder="$t(queryPlaceholder)"
        @keyup.enter.prevent="handleInput"
      />
      <el-popover
        v-if="showQueryFilter"
        popper-class="filter-popover"
        placement="bottom-end"
        trigger="click"
        width="320px"
      >
        <template #reference>
          <img src="../../../assets/images/table/filter.png" alt="" />
        </template>
        <div class="query-filter">
          <slot name="queryFilter" />
        </div>
      </el-popover>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, unref, watch, toRefs } from 'vue';
import type { FilterProps } from '../BasicTable';

const props = {
  filterOptions: {
    type: Object,
    default: {
      showQuery: false,
      showQueryFilter: true,
      showColumnFilter: false,
      filterColumns: [],
      showToolbar: true,
      queryPlaceholder: '',
    },
  },
  keyword: {
    type: String,
    default: '',
  },
};

export default defineComponent({
  name: 'TableToolbar',
  props,
  emits: ['columnFilterChange', 'search', 'input'],
  setup(props, { emit }) {
    const { filterOptions, keyword } = toRefs(props);
    const filterColumns = unref(filterOptions)?.filterColumns;
    const showColumnFilter = unref(filterOptions).showColumnFilter;
    const showToolbar = unref(filterOptions).showToolbar;
    const queryPlaceholder = unref(filterOptions).queryPlaceholder;
    const showQueryFilter =
      typeof unref(filterOptions).showQueryFilter != 'undefined'
        ? unref(filterOptions).showQueryFilter
        : true;
    const showQuery =
      typeof unref(filterOptions).showQuery != 'undefined' ? unref(filterOptions).showQuery : false;
    const width = ref('300px');
    const checkList = ref<string[]>([]);
    const checkAll = ref(false);
    const isIndeterminate = ref(true);
    const searVal = ref<HTMLInputElement | null>(null);

    const originCheckList = filterColumns
      ? unref(filterColumns)
          .map((item: any) => {
            if (item.require) {
              return item['label'];
            }
          })
          .filter((item: any) => {
            if (typeof item !== 'undefined') return item;
          })
      : [];
    checkList.value = originCheckList as string[];

    const handleCheckAllChange = (val: boolean) => {
      checkList.value = val
        ? unref(filterColumns).map((item: any) => {
            return item['label'];
          })
        : (originCheckList as string[]);
      const checkedCount = checkList.value.length;
      isIndeterminate.value = checkedCount > 0 && checkedCount < unref(filterColumns).length;
    };

    const handleCheckedOptionsChange = (value: string[]) => {
      const checkedCount = value.length;
      checkAll.value = checkedCount === unref(filterColumns).length;
      isIndeterminate.value = checkedCount > 0 && checkedCount < unref(filterColumns).length;
    };

    function group(array: any[], subGroupLength: number) {
      let index = 0;
      const newArray: any[][] = [];

      while (index < array.length) {
        newArray.push(array.slice(index, (index += subGroupLength)));
      }

      return newArray;
    }

    const dataList = ref(group(filterColumns ? unref(filterColumns) : [], 10));
    width.value = `${300 * dataList.value.length}px`;

    watch(checkList, (value) => {
      if (value) {
        emit('columnFilterChange', value);
      }
    });

    const handleInput = () => {
      emit('search', searVal.value?.value);
    };
    const handleonInput = () => {
      emit('input', searVal.value?.value);
    };

    return {
      showQuery,
      showQueryFilter,
      queryPlaceholder,
      showToolbar,
      showColumnFilter,
      keyword,
      handleInput,
      searVal,
      width,
      checkList,
      dataList,
      checkAll,
      isIndeterminate,
      handleCheckAllChange,
      handleCheckedOptionsChange,
      handleonInput,
    };
  },
});
</script>

<style scoped lang="scss">
.table-toolbar {
  display: flex;
  justify-content: space-between;
  height: 40px;
  margin-bottom: 10px;

  .left-wrapper {
    display: flex;
  }

  .column-filter {
    img {
      width: 32px;
      height: 32px;
      cursor: pointer;
    }
  }

  .search-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-around;
    width: 274px;
    height: 32px;
    background-color: #f5f7fa;
    border-radius: 4px;

    input {
      width: 200px;
      height: 100%;
      font-size: 14px;
      outline: none;
      background: none;
      border: 0;

      &:focus {
        outline: none;
      }

      &::placeholder {
        font-size: 14px;
        color: #999;
      }
    }

    img {
      width: 20px;
      height: 20px;

      &:last-child {
        cursor: pointer;
      }
    }
  }
}
</style>
<style lang="scss" scoped>
.el-popover.filter-popover {
  padding: 0 !important;

  .el-popper__arrow {
    display: none !important;
  }

  .list-wrapper {
    .list-title {
      height: 40px;
      padding: 0 10px;
      font-size: 14px;
      font-weight: 500;
      line-height: 40px;
      color: #333;
      border-bottom: 1px solid #f5f7fa;
    }

    .list-data {
      .check-all {
        padding: 0 10px;
      }

      .list-row {
        display: flex;

        .list-col {
          width: 290px !important;
          padding-right: 10px;
          padding-bottom: 10px;
          padding-left: 10px;
          border-right: 1px solid #f5f7fa;

          &:last-child {
            border: 0;
          }

          .el-checkbox {
            min-width: 240px !important;
          }

          .el-checkbox__label {
            width: 240px !important;
            min-width: 240px !important;
            padding-right: 5px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
      }
    }
  }

  .query-filter {
    padding: 10px;
  }
}
</style>
